/**
 * Shared resolution for the "show these custom fields as columns/rows" config
 * (`leadCardFunctionality.customFieldToggles`, and the dynamic-module twin).
 *
 * The toggle map stores CF **ids**, so it outlives the fields themselves: delete
 * a custom field and every browser that had it ticked keeps a dead id in its
 * persisted config. Rendering that id produced a column headed `Field 178` with
 * an em-dash in every row — a field that cannot have a name, because it no
 * longer exists.
 *
 * Two ids can fail to resolve, and they need opposite treatment:
 *   • the catalog hasn't loaded yet    → keep the column, label it {@link LOADING_CF_LABEL}
 *   • the catalog HAS loaded, no match → the field is gone, drop the column
 *
 * Which is why `catalogReady` is a required argument rather than an inferred
 * `map.size > 0`: an empty map during loading would otherwise wipe every
 * custom-field column and then flash them all back in.
 *
 * IMPORTANT: the caller's label map MUST be built from catalogs fetched with
 * `allCampaigns: true` for BOTH `leads` and `child`. Campaign-scoped (Meta
 * question) fields are omitted otherwise, and a live field would then look
 * "deleted" to this helper and be silently dropped.
 */

/** Placeholder shown only while the custom-field catalog is still loading. */
export const LOADING_CF_LABEL = "…";

/**
 * Numeric ids switched on in ANY `{ "<id>": boolean }` toggle map, ascending.
 * Not custom-field specific — role toggles use the same shape, and they must
 * NOT be validated against the custom-field catalog.
 */
export function enabledToggleIds(
  toggles: Record<string, boolean> | undefined | null,
): number[] {
  return Object.entries(toggles ?? {})
    .filter(([, on]) => Boolean(on))
    .map(([id]) => Number(id))
    .filter((n) => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);
}

/**
 * The toggled custom-field ids that should actually be RENDERED, in display
 * order (ascending id — the order the settings UI lists them in).
 *
 * Use this for display only. Request parameters that merely ask the server for
 * values keep {@link enabledToggleIds}: a dead id costs nothing there, whereas
 * filtering would change the query key the moment the catalog resolves and
 * cause an extra refetch on every cold load.
 */
export function resolvedCfIds(
  toggles: Record<string, boolean> | undefined | null,
  cfLabelById: Map<number, string>,
  catalogReady: boolean,
): number[] {
  // Only prune once we can tell "deleted" from "not loaded yet".
  return enabledToggleIds(toggles).filter(
    (n) => !catalogReady || cfLabelById.has(n),
  );
}

/**
 * Display label for an id that survived {@link resolvedCfIds}. Falls back to
 * the loading placeholder — never to a raw `Field <id>`, which only ever told
 * the user a database id they can do nothing with.
 */
export function cfColumnLabel(
  id: number,
  cfLabelById: Map<number, string>,
): string {
  return cfLabelById.get(id) ?? LOADING_CF_LABEL;
}
